import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ExecutionOutput = ({ 
  logs, 
  screenshots, 
  validationResults, 
  executionMetrics, 
  isRunning 
}) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const logsEndRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'logs' && logsEndRef?.current) {
      logsEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, activeTab]);

  const tabs = [
    { id: 'logs', label: 'Execution Logs', icon: 'Terminal', count: logs?.length },
    { id: 'screenshots', label: 'Screenshots', icon: 'Camera', count: screenshots?.length },
    { id: 'validation', label: 'Validation', icon: 'CheckCircle2', count: validationResults?.length },
    { id: 'metrics', label: 'Metrics', icon: 'BarChart3', count: null }
  ];

  const getLogTypeIcon = (type) => {
    switch (type) {
      case 'info': return 'Info';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'AlertCircle';
      case 'success': return 'CheckCircle2';
      default: return 'Terminal';
    }
  };

  const getLogTypeColor = (type) => {
    switch (type) {
      case 'info': return 'text-primary';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      case 'success': return 'text-success';
      default: return 'text-foreground';
    }
  };

  const getValidationStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'text-success bg-success/10';
      case 'failed': return 'text-error bg-error/10';
      case 'warning': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/20';
    }
  };

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Output Header */}
      <div className="flex items-center justify-between p-3 border-b border-subtle">
        <div className="flex items-center space-x-1">
          {tabs?.map((tab) => (
            <Button
              key={tab?.id}
              variant={activeTab === tab?.id ? "default" : "ghost"}
              size="sm"
              iconName={tab?.icon}
              iconPosition="left"
              onClick={() => setActiveTab(tab?.id)}
              className="text-xs"
            >
              {tab?.label}
              {tab?.count !== null && (
                <span className="ml-1 px-1.5 py-0.5 bg-muted rounded text-xs">
                  {tab?.count}
                </span>
              )}
            </Button>
          ))}
        </div>
        
        <div className="flex items-center space-x-2">
          {isRunning && (
            <div className="flex items-center space-x-2 text-xs text-primary">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span>Executing...</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      {/* Output Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'logs' && (
          <div className="h-full overflow-y-auto p-3 space-y-2">
            {logs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Icon name="Terminal" size={48} className="mb-3 opacity-50" />
                <p className="text-sm">No execution logs yet</p>
                <p className="text-xs">Run your code to see output here</p>
              </div>
            ) : (
              <>
                {logs?.map((log, index) => (
                  <div key={index} className="flex items-start space-x-3 text-sm">
                    <div className="flex items-center space-x-2 min-w-[120px] text-xs text-muted-foreground">
                      <span>{log?.timestamp}</span>
                    </div>
                    <Icon 
                      name={getLogTypeIcon(log?.type)} 
                      size={16} 
                      className={getLogTypeColor(log?.type)}
                    />
                    <div className="flex-1">
                      <pre className="whitespace-pre-wrap font-mono text-xs text-foreground">
                        {log?.message}
                      </pre>
                    </div>
                  </div>
                ))}
                <div ref={logsEndRef} />
              </>
            )}
          </div>
        )}

        {activeTab === 'screenshots' && (
          <div className="h-full overflow-y-auto p-3">
            {screenshots?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Icon name="Camera" size={48} className="mb-3 opacity-50" />
                <p className="text-sm">No screenshots captured</p>
                <p className="text-xs">Screenshots will appear here during execution</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {screenshots?.map((screenshot, index) => (
                  <div key={index} className="bg-muted/30 rounded-lg overflow-hidden">
                    <div className="aspect-video relative cursor-pointer hover:opacity-80 transition-opacity"
                         onClick={() => setSelectedScreenshot(screenshot)}>
                      <Image
                        src={screenshot?.url}
                        alt={screenshot?.description}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Icon name="ZoomIn" size={24} color="white" />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-foreground mb-1">
                        {screenshot?.step}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {screenshot?.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {screenshot?.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'validation' && (
          <div className="h-full overflow-y-auto p-3 space-y-3">
            {validationResults?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Icon name="CheckCircle2" size={48} className="mb-3 opacity-50" />
                <p className="text-sm">No validation results</p>
                <p className="text-xs">Run your code to see validation feedback</p>
              </div>
            ) : (
              validationResults?.map((result, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getValidationStatusColor(result?.status)}`}>
                        {result?.status?.toUpperCase()}
                      </span>
                      <h3 className="text-sm font-medium text-foreground">
                        {result?.testName}
                      </h3>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {result?.executionTime}ms
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">
                    {result?.description}
                  </p>
                  
                  {result?.details && (
                    <div className="bg-background/50 rounded p-3">
                      <pre className="text-xs font-mono text-foreground whitespace-pre-wrap">
                        {result?.details}
                      </pre>
                    </div>
                  )}
                  
                  {result?.suggestions && result?.suggestions?.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-medium text-foreground mb-2">Suggestions:</h4>
                      <ul className="space-y-1">
                        {result?.suggestions?.map((suggestion, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-xs text-muted-foreground">
                            <Icon name="Lightbulb" size={12} className="mt-0.5 text-warning" />
                            <span>{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="h-full overflow-y-auto p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Clock" size={20} className="text-primary" />
                  <h3 className="text-sm font-medium text-foreground">Execution Time</h3>
                </div>
                <div className="text-2xl font-bold text-foreground mb-1">
                  {executionMetrics?.executionTime || '0'}ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Total runtime for this execution
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Cpu" size={20} className="text-secondary" />
                  <h3 className="text-sm font-medium text-foreground">Memory Usage</h3>
                </div>
                <div className="text-2xl font-bold text-foreground mb-1">
                  {executionMetrics?.memoryUsage || '0'}MB
                </div>
                <p className="text-xs text-muted-foreground">
                  Peak memory consumption
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Globe" size={20} className="text-accent" />
                  <h3 className="text-sm font-medium text-foreground">Network Requests</h3>
                </div>
                <div className="text-2xl font-bold text-foreground mb-1">
                  {executionMetrics?.networkRequests || '0'}
                </div>
                <p className="text-xs text-muted-foreground">
                  HTTP requests made
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Activity" size={20} className="text-warning" />
                  <h3 className="text-sm font-medium text-foreground">Container Status</h3>
                </div>
                <div className="text-lg font-bold text-success mb-1">
                  {executionMetrics?.containerStatus || 'Idle'}
                </div>
                <p className="text-xs text-muted-foreground">
                  Current container state
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Screenshot Modal */}
      {selectedScreenshot && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
             onClick={() => setSelectedScreenshot(null)}>
          <div className="bg-card rounded-lg max-w-4xl max-h-full overflow-auto"
               onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-subtle">
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  {selectedScreenshot?.step}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedScreenshot?.description}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedScreenshot(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="p-4">
              <Image
                src={selectedScreenshot?.url}
                alt={selectedScreenshot?.description}
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionOutput;